import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  CreateMorningSlotInput,
  MorningCityConfigDto,
  MorningCityConfigUpdateInput,
  MorningHolidayCreateInput,
  MorningHolidayDto,
  MorningHolidayUpdateInput,
  MorningImageDto,
  MorningImageUpdateInput,
  MorningPhraseCreateInput,
  MorningPhraseDto,
  MorningPhraseUpdateInput,
  MorningSettingsDto,
  MorningSettingsUpdateInput,
  MorningSlotDto,
} from '@upme/contracts';
import { api } from '../../app/api-client';
import { morningKey } from './queries';

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MorningSettingsUpdateInput) =>
      api.patch('admin/morning/settings', { json: body }).json<MorningSettingsDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'settings'] }),
  });
}

export function useCreatePhrase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MorningPhraseCreateInput) =>
      api.post('admin/morning/phrases', { json: body }).json<MorningPhraseDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'phrases'] }),
  });
}

export function useUpdatePhrase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MorningPhraseUpdateInput }) =>
      api.patch(`admin/morning/phrases/${id}`, { json: body }).json<MorningPhraseDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'phrases'] }),
  });
}

export function useDeletePhrase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`admin/morning/phrases/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'phrases'] }),
  });
}

export function useUploadImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, cityId, label }: { file: File; cityId: string | null; label?: string }) => {
      const form = new FormData();
      form.append('file', file);
      if (cityId) form.append('cityId', cityId);
      if (label) form.append('label', label);
      return api.post('admin/morning/images', { body: form }).json<MorningImageDto>();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'images'] }),
  });
}

export function useUpdateImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MorningImageUpdateInput }) =>
      api.patch(`admin/morning/images/${id}`, { json: body }).json<MorningImageDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'images'] }),
  });
}

export function useDeleteImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`admin/morning/images/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'images'] }),
  });
}

export function useUpdateCityConfig(cityId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MorningCityConfigUpdateInput) =>
      api.patch(`admin/morning/cities/${cityId}/config`, { json: body }).json<MorningCityConfigDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'city-config', cityId] }),
  });
}

export function useCreateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MorningHolidayCreateInput) =>
      api.post('admin/morning/holidays', { json: body }).json<MorningHolidayDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'holidays'] }),
  });
}

export function useUpdateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MorningHolidayUpdateInput }) =>
      api.patch(`admin/morning/holidays/${id}`, { json: body }).json<MorningHolidayDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'holidays'] }),
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`admin/morning/holidays/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'holidays'] }),
  });
}

export function useUploadHolidayImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const form = new FormData();
      form.append('file', file);
      return api.post(`admin/morning/holidays/${id}/image`, { body: form }).json<MorningHolidayDto>();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'holidays'] }),
  });
}

export function useCreateMorningSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMorningSlotInput) =>
      api.post('admin/morning/slots', { json: body }).json<MorningSlotDto>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'slots'] }),
  });
}

export function useDeleteMorningSlot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`admin/morning/slots/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: [...morningKey, 'slots'] }),
  });
}

export function useTestSend() {
  return useMutation({
    mutationFn: ({ cityId, date }: { cityId: string; date?: string }) =>
      api.post('admin/morning/test-send', { json: { cityId, date } }).json<{ ok: boolean }>(),
  });
}
